import styled, { css, DefaultTheme } from 'styled-components/macro';
import { StatusProps, StatusVariants } from './status.types';
import { DSScaledColors } from 'styles';

const indicationModes = {
  primary: 'info',
  gray: 'neutral',
  green: 'success',
  orange: 'warning',
  red: 'error',
  purple: 'super',
} as const satisfies Record<keyof DSScaledColors, string>;

const getVariantStyles = (
  theme: DefaultTheme,
  color: keyof DSScaledColors,
  variant: StatusVariants,
  disabled?: boolean
) => css`
  /* У outline/minor сейчас фон [100]; роли bg-subtle/bg-minor дают
     другие значения. Эти изменения цвета требуют решения дизайна. */
  background-color: ${disabled
    ? theme.semantic.indication.bgDisabled
    : variant === 'main'
      ? theme.semantic.indication[indicationModes[color]].bg
      : theme.colors[color][100]};
  border-color: ${!disabled && variant === 'outline'
    ? theme.semantic.indication[indicationModes[color]].borderStrong
    : 'transparent'};
  ${variant === 'ghost' &&
  !disabled &&
  'background-color: transparent; border-color: transparent; '}
`;

const getColorVariants = (
  theme: DefaultTheme,
  color: keyof DSScaledColors,
  disabled?: boolean
): Record<StatusVariants, ReturnType<typeof css>> => ({
  main: getVariantStyles(theme, color, 'main', disabled),
  outline: getVariantStyles(theme, color, 'outline', disabled),
  minor: getVariantStyles(theme, color, 'minor', disabled),
  ghost: getVariantStyles(theme, color, 'ghost', disabled),
});

const colors: (keyof DSScaledColors)[] = [
  'primary',
  'gray',
  'red',
  'green',
  'orange',
  'purple',
];

const Variants = (theme: DefaultTheme, disabled?: boolean) =>
  Object.fromEntries(
    colors.map((color) => [color, getColorVariants(theme, color, disabled)])
  ) as Record<
    keyof DSScaledColors,
    Record<StatusVariants, ReturnType<typeof css>>
  >;

const PointVariants = (
  theme: DefaultTheme
): Record<keyof DSScaledColors, string> => ({
  primary: theme.semantic.icon.info,
  gray: theme.semantic.icon.neutral,
  red: theme.semantic.icon.error,
  green: theme.semantic.icon.success,
  orange: theme.semantic.icon.warning,
  purple: theme.semantic.icon.super,
});

export const StyledStatus = styled.div<StatusProps>`
  ${({ theme, size, type, color, variant, disabled }) => css`
    ${Variants(theme, disabled)[color || 'primary'][variant || 'main']}};

    box-sizing: border-box;
    padding-inline: ${theme.spacings.s};
    display: flex;
    align-items: center;
    column-gap: ${theme.spacings.xs};
    width: fit-content;
    height: ${size === 'big' ? '32px' : '24px'};
    color: ${
      disabled
        ? theme.semantic.indication.textDisabled
        : theme.semantic.text.default
    };
    border-radius: ${
      type === 'square' ? theme.radiuses.xxs : theme.radiuses.full
    };
    border-width: 1px;
    border-style: solid;
    overflow: hidden;
    transition-duration: ${theme.tokens.animation.duration.base};
  `}
`;

export const Point = styled.div<Pick<StatusProps, 'color' | 'disabled'>>`
  ${({ theme, color, disabled }) => css`
    min-width: 8px;
    min-height: 8px;
    background-color: ${disabled
      ? theme.semantic.icon.disabled
      : PointVariants(theme)[color || 'primary']};
    border-radius: 50%;
  `}
`;

StyledStatus.defaultProps = {
  color: 'primary',
  variant: 'main',
  size: 'big',
  type: 'square',
};

export const StatusText = styled.p.attrs({
  className: 'button-14',
})`
  width: 100%;
  white-space: nowrap;
`;
